import React from 'react'
import { useHistory, useParams } from 'react-router-dom'
import {
  type CurrencyExchangeInstructionBatchDetails
} from '../../../../api-schema-types/financial/CurrencyExchangeDomainDtos'
import { useGetBatchQuery } from '../../../../api-services/financial/currencyExchangeApi'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import CurrencyCodeChip from '../../../components/chips/CurrencyCodeChip'
import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'
import LoadingComponent from '../../../components/ui-widgets/LoadingComponent'
import CancelBatch from './CancelBatch'
import { setUnlinkActions } from './exchangeInstructionsSlice'
import { selectUnlinkActions, type UnlinkAction } from './exchangeInstructionsSlice'

import { Box, Button, Grid, Typography, Paper, Checkbox } from '@mui/material'
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Refresh from '@mui/icons-material/Refresh'

import InstructionStateChip from './InstructionStateChip'
import ProcessBatch from './ProcessBatch'
import UnlinkInstructions from './UnlinkInstructions'

export interface BatchIdActionProps {
  batch: CurrencyExchangeInstructionBatchDetails
}

const ExchangeInstructionBatchPage: React.FC = () => {
  const params = useParams<any>()
  const history = useHistory()
  const dispatch = useAppDispatch()
  const unlinkActions = useAppSelector<UnlinkAction[]>(selectUnlinkActions)

  // =================================================================================================================
  //  Get Batch query

  const batchId: number = params.batchId
  let batch: CurrencyExchangeInstructionBatchDetails | null = null

  const getBatchApiResult = useGetBatchQuery({ batchId })
  const refetchBatch = getBatchApiResult.refetch

  if (getBatchApiResult.error !== null && !getBatchApiResult.isLoading && (getBatchApiResult.data != null)) {
    batch = getBatchApiResult.data
  }

  // =================================================================================================================

  // Checkbox handling !!
  const handleCheckboxClick = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const dataIndex = event.target.getAttribute('itemID')
    if (dataIndex === null) {
      console.error('No data-instruction-id attribute')
      return
    }

    const currentOptions: UnlinkAction[] = [...unlinkActions].filter((ua) => ua.instructionId !== parseInt(dataIndex))
    if (event.target.checked) {
      currentOptions.push({ instructionId: parseInt(dataIndex), checked: event.target.checked })
    } else {
      // We've filtered out the checkbox, it is false so do nothing
    }
    dispatch(setUnlinkActions(currentOptions))
  }

  // =================================================================================================================

  const instructionColumns: GridColDef[] = [
    { field: 'currencyExchangeInstructionId', headerName: 'ID', width: 35, editable: false },
    {
      field: 'fromCurrencyCode',
      headerName: 'From Currency',
      width: 120,
      editable: false,
      renderCell: params => <CurrencyCodeChip code={params.row.fromCurrencyCode} name={`${params.row.fromCurrencyCode}`}/>
    },
    {
      field: 'toCurrencyCode',
      headerName: 'To Currency',
      width: 120,
      editable: false,
      renderCell: params => <CurrencyCodeChip code={params.row.toCurrencyCode} name={`${params.row.toCurrencyCode}`}/>
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 50,
      editable: false,
      renderCell: params => <InstructionStateChip state={params.row.state}/>
    },
    { field: 'assetHolderId', headerName: 'Member ID', flex: 50, editable: false },
    { field: 'assetHolderEmailAddress', headerName: 'Email address', flex: 80, editable: false },
    { field: 'fromCurrencyAmountFormatted', headerName: 'From Amount', flex: 50, editable: false, align: 'right' },
    { field: 'toCurrencyAmountFormatted', headerName: 'To Amount', flex: 50, editable: false, align: 'right' },
    { field: 'exchangeRate', headerName: 'Exchange Rate', flex: 50, editable: false, align: 'right' },
    // { field: 'feeAmountFormatted', headerName: 'Fee Amount', flex: 50, editable: false, align: 'right' },
    // { field: 'payoutAmountFormatted', headerName: 'Payout Amount', flex: 50, editable: false, align: 'right' },
    // { field: 'confirmedAtFormatted', headerName: 'Confirmed At', flex: 50, editable: false },
    {
      field: 'unlinkInstruction',
      headerName: 'Unlink',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Checkbox value={params.row.actionUnlink} onChange={handleCheckboxClick} inputProps={{ itemID: params.row.currencyExchangeInstructionId }}/>
        )
      }
    }
  ]

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <Typography variant={'h4'}>Disbursement
          Batch {batch !== null ? `${batch.batchId} - ${batch.fromCurrencyCode}:${batch.toCurrencyCode}` : ''}</Typography>
        <Button variant={'outlined'} onClick={() => history.push('/app/finance-area/currency-exchange-instructions/batches')}
                startIcon={<ArrowBackIcon/>}>
          Batches
        </Button>
      </Box>

      <Paper>
        <Grid container justifyContent="right" sx={{ mb: 2, pr: 4, pt: 4 }}>
          <Grid item>
            <Button variant={'contained'} type="button" onClick={refetchBatch}>
              <Refresh/>
            </Button>
          </Grid>
        </Grid>

        {getBatchApiResult.isLoading && <LoadingComponent/>}
        {getBatchApiResult.isError &&
          <ErrorComponent message={RtkQueryHelper.mapResponseError(getBatchApiResult.error)}/>}
        {getBatchApiResult.isSuccess && (
          <>
            <Box sx={{ pl: 4, pr: 4, pt: 2 }}>
              <h3>Batch Details</h3>
              <Grid container spacing={4}>
                <Grid item md={2}>
                  <Typography variant={'body2'}><strong>State</strong></Typography>
                  <InstructionStateChip state={getBatchApiResult.data.state}/>
                </Grid>
                <Grid item md={4}>
                  <Typography variant={'body2'}><strong>Details</strong></Typography>
                  <Typography variant={'subtitle1'}>
                    Convert: <CurrencyCodeChip code={getBatchApiResult.data.fromCurrencyCode} name={`${getBatchApiResult.data.fromCurrencyCode}`}/> : <CurrencyCodeChip code={getBatchApiResult.data.toCurrencyCode} name={`${getBatchApiResult.data.toCurrencyCode}`}/>
                  </Typography>
                  <Typography variant={'subtitle1'}>
                    From Amount: {getBatchApiResult.data.totalFromCurrencyAmountFormatted}
                  </Typography>
                  <Typography variant={'subtitle1'}>
                    To Amount: {getBatchApiResult.data.totalToCurrencyAmountFormatted}
                  </Typography>
                  <Typography variant={'subtitle1'}>
                    Exchange Rate: {getBatchApiResult.data.exchangeRate}
                  </Typography>
                  <Typography variant={'subtitle1'}>
                    {getBatchApiResult.data.countInstructions} instructions
                  </Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography variant={'body2'}><strong>Created</strong></Typography>
                  <Typography variant={'subtitle1'}>
                    {getBatchApiResult.data.createdAtFormatted}
                  </Typography>
                  <Typography variant={'subtitle1'}>
                    {getBatchApiResult.data.createdByName}
                  </Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography variant={'body2'}><strong>Processed</strong></Typography>
                  <Typography variant={'subtitle1'}>
                    {getBatchApiResult.data.processedAtFormatted}
                  </Typography>
                  <Typography variant={'subtitle1'}>
                    {getBatchApiResult.data.processedByName}
                  </Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography variant={'body2'}><strong>Cancelled</strong></Typography>
                  <Typography variant={'subtitle1'}>
                    {getBatchApiResult.data.cancelledAtFormatted}
                  </Typography>
                  <Typography variant={'subtitle1'}>
                    {getBatchApiResult.data.cancelledByName}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ pl: 4, pr: 4, pt: 0 }}>
              <h3>Actions</h3>
              <Grid container spacing={2} alignItems={'center'}>
                <Grid item md={4}>
                  <UnlinkInstructions batch={getBatchApiResult.data}
                                      instructionIds={unlinkActions.map((ua) => ua.instructionId)} />
                </Grid>
                <Grid item md={4} sx={{ textAlign: 'left' }}>
                  <ProcessBatch batch={getBatchApiResult.data} />
                </Grid>
                <Grid item md={4} sx={{ textAlign: 'right' }}>
                  <CancelBatch batch={getBatchApiResult.data} />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ pl: 4, pr: 4, pt: 2, pb: 2 }}>
              <h3>Pending Currency Exchange Instructions for this batch</h3>
              <DataGrid
                getRowId={(row) => row.currencyExchangeInstructionId}
                rows={getBatchApiResult.data.instructions}
                columns={instructionColumns}
                autoHeight={true}
                disableSelectionOnClick

                pagination
                pageSize={15}
                rowsPerPageOptions={[15]}
              />
            </Box>
          </>
        )}

      </Paper>
    </>

  )
}

export default ExchangeInstructionBatchPage
